import React from 'react';
import { Card, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { FaCalendarAlt, FaNewspaper } from 'react-icons/fa';

const NewsAndEvents = ({ 
  campusEvents, 
  latestNews, 
  loadingEvents, 
  loadingNews,
  formatDate 
}) => {
  return (
    <Row className="g-4">
      {/* Campus Events Card */}
      <Col xs={12} lg={6}>
        <Card className="events-card h-100">
          <Card.Header className="events-header py-3">
            <h3 className="mb-0 fs-5 d-flex align-items-center">
              <FaCalendarAlt className="me-2" /> 
              Campus Events
              <span className="blink-text ms-2">Live</span>
            </h3>
          </Card.Header>
          <Card.Body className="p-3">
            {loadingEvents ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 mb-0 text-muted">Loading events...</p>
              </div>
            ) : campusEvents.length > 0 ? (
              <div className="list-group list-group-flush">
                {campusEvents.map(event => (
                  <div key={event.id} className="list-group-item border-0 px-0 py-3">
                    {event.imageUrl && (
                      <img 
                        src={event.imageUrl} 
                        alt={event.title} 
                        className="img-fluid rounded mb-2" 
                        style={{ maxHeight: '150px', width: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.onerror = null; 
                          e.target.src = 'https://via.placeholder.com/300x200?text=Event+Image';
                        }}
                      />
                    )}
                    <h5 className="mb-1 fw-bold text-primary">{event.title}</h5>
                    <p className="text-muted mb-2">{event.description}</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-primary fw-semibold">
                        <FaCalendarAlt className="me-1" />
                        {formatDate(event.date)}
                      </small>
                      <Badge bg="warning" text="dark">Event</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                <FaCalendarAlt size={48} className="mb-2 opacity-50" />
                <p className="mb-0">No upcoming events scheduled</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Latest News Card */}
      <Col xs={12} lg={6}>
        <Card className="news-card h-100">
          <Card.Header className="news-header py-3">
            <h3 className="mb-0 fs-5 d-flex align-items-center">
              <FaNewspaper className="me-2" /> 
              Latest News
              <span className="blink-text ms-2">New</span>
            </h3>
          </Card.Header>
          <Card.Body className="p-3">
            {loadingNews ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="success" />
                <p className="mt-2 mb-0 text-muted">Loading news...</p>
              </div>
            ) : latestNews.length > 0 ? (
              <div className="list-group list-group-flush">
                {latestNews.map(news => (
                  <div key={news.id} className="list-group-item border-0 px-0 py-3">
                    {news.imageUrl && (
                      <img 
                        src={news.imageUrl} 
                        alt={news.title} 
                        className="img-fluid rounded mb-2" 
                        style={{ maxHeight: '150px', width: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.onerror = null; 
                          e.target.src = 'https://via.placeholder.com/300x200?text=News+Image';
                        }}
                      />
                    )}
                    <h5 className="mb-1 fw-bold text-success">{news.title}</h5>
                    <p className="text-muted mb-2">{news.description}</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-success fw-semibold">
                        <FaCalendarAlt className="me-1" />
                        {formatDate(news.date)}
                      </small>
                      <Badge bg="info">News</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                <FaNewspaper size={48} className="mb-2 opacity-50" />
                <p className="mb-0">No latest news available</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <style jsx>{`
        .events-header {
          background: linear-gradient(135deg, #8d2020 0%, #b71c1c 100%) !important;
          color: white !important;
          border-bottom: none;
        }
        
        .news-header {
          background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%) !important;
          color: white !important;
          border-bottom: none;
        }
        
        @keyframes blink {
          0% { 
            opacity: 1;
            color: #ffffff;
            background-color: #ff0000;
            box-shadow: 0 0 10px #ff0000, 0 0 20px #ff0000;
          }
          50% { 
            opacity: 0.8;
            color: #ffeb3b;
            background-color: #ff4444;
            box-shadow: 0 0 15px #ff0000, 0 0 30px #ff0000, 0 0 45px #ff0000;
            transform: scale(1.05);
          }
          100% { 
            opacity: 1;
            color: #ffffff;
            background-color: #ff0000;
            box-shadow: 0 0 10px #ff0000, 0 0 20px #ff0000;
          }
        }
        
        .blink-text {
          animation: blink 1.5s infinite;
          font-weight: bold;
          padding: 2px 8px;
          border-radius: 12px;
          display: inline-block;
          margin-left: 8px;
          border: 1px solid #ffffff;
          text-shadow: 0 0 5px rgba(0,0,0,0.5);
          position: relative;
          z-index: 1;
          font-size: 0.75rem;
        }
        
        .blink-text:hover {
          animation-duration: 0.7s;
        }
        
        .card {
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
          border: 1px solid rgba(0,0,0,0.1);
        }
        
        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }
        
        .list-group-item {
          transition: background-color 0.2s ease;
          border-bottom: 1px solid rgba(0,0,0,0.05) !important;
        }
        
        .list-group-item:hover {
          background-color: #f8f9fa;
        }
        
        .list-group-item:last-child {
          border-bottom: none !important;
        }
      `}</style>
    </Row>
  );
};

export default NewsAndEvents;